import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    CompanyTypeView,
    ContactView,
    MarketView,
    QuickSearchContactsParameters,
    SearchUsersParameters,
    UserView
} from '@api-proxies';
import { CompanyTypeService } from '@http/clients/company-type.service';
import { ContactService } from '@http/clients/contact.service';
import { MarketService } from '@http/clients/market.service';
import { UserService } from '@http/clients/user.service';
import { FilterStateService } from '@services/filter-state.service';
import { AdditionalFiltersComponent } from '@shared/additional-filters/additional-filters.component';
import { ClearFiltersButtonComponent } from '@shared/clear-filters-button/clear-filters-button.component';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { ITypeAheadService } from '@shared/generic-type-ahead/generic-type-ahead.component';
import { FilterService, IGetById, SearchInputComponent } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { COMPANIES_FILTER_CONFIG, COMPANIES_FILTER_KEY } from './companies-filter.config';

export interface IFilterValueCompanies {
    searchText?: string;
    companyType?: CompanyTypeView;
    market?: MarketView;
    contact?: ContactView;
    includeArchived?: boolean;
    sortBy?: string;
    sortDirection?: string;
    salespersons?: UserView[];
}

@Component({
    selector: 'app-companies-filters',
    templateUrl: './companies-filters.component.html',
    styleUrls: ['./companies-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SearchInputComponent, ClearFiltersButtonComponent, CustomMenuItemComponent, AdditionalFiltersComponent]
})
export class CompaniesFiltersComponent implements OnInit, OnDestroy {
    filtersKey = COMPANIES_FILTER_KEY;

    filters: IFilterValueCompanies = {};

    companyTypes: CompanyTypeView[] = [];

    markets: MarketView[] = [];

    contactServiceFilter?: ITypeAheadService<ContactView, QuickSearchContactsParameters>;
    userServiceFilter?: ITypeAheadService<UserView, SearchUsersParameters>;

    disabledClearFiltersBtn = false;

    additionalFiltersConfig = COMPANIES_FILTER_CONFIG;

    private readonly filterStateService = inject(FilterStateService<IFilterValueCompanies>);
    private readonly contactService = inject(ContactService);
    private readonly companyTypeService = inject(CompanyTypeService);
    private readonly marketService = inject(MarketService);
    private readonly filterService = inject(FilterService);
    private readonly userService = inject(UserService);
    private readonly activatedRoute = inject(ActivatedRoute);

    private filterSubscription?: Subscription;

    async ngOnInit(): Promise<void> {
        this.companyTypes = await this.companyTypeService.getAll();
        this.markets = await this.marketService.getAll();
        this.contactServiceFilter = this.contactService;
        this.userServiceFilter = this.userService;
        await this.deserializeUrlParams();

        if (Object.keys(this.activatedRoute.snapshot.queryParams).length === 0) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(
                this.filtersKey
            ) as IFilterValueCompanies;
            this.filters = {
                ...this.filters,
                ...(filtersFromLocalStorage ? filtersFromLocalStorage : {})
            };
        }

        this.filterStateService.updateFilters(this.filters);

        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (filters) => {
            if (!filters) {
                this.filters = {} as IFilterValueCompanies;
            } else {
                this.filters = filters as IFilterValueCompanies;
                await this.updateFilterUrlParams();
                this.disabledClearFiltersBtn = !this.someFilterApplied();
            }
            this.filterStateService.saveToLocalStorage(this.filtersKey, this.filters);
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    onFilterChanged(): void {
        this.filterStateService.updateMainFilters(this.filters);
    }

    private async updateFilterUrlParams(): Promise<void> {
        await this.filterService.serialize<IFilterValueCompanies>(this.filters, 'sortBy');
        await this.filterService.serialize<IFilterValueCompanies>(this.filters, 'sortDirection');
        await this.filterService.serialize<IFilterValueCompanies>(this.filters, 'searchText');
        await this.filterService.serializeTypeAhead<IFilterValueCompanies, ContactView>(
            'contact',
            this.filters.contact,
            'id'
        );
        await this.filterService.serializeArray<IFilterValueCompanies, CompanyTypeView>(
            this.filters,
            'companyType',
            'id'
        );
        await this.filterService.serializeArray<IFilterValueCompanies, MarketView>(this.filters, 'market', 'id');
        await this.filterService.serializeBoolean<IFilterValueCompanies>(this.filters, 'includeArchived');

        await this.filterService.serializeTypeAhead<IFilterValueCompanies, UserView>(
            'salespersons',
            this.filters.salespersons,
            'externalId',
            true
        );
    }

    private async deserializeUrlParams() {
        this.filterService.deserialize<IFilterValueCompanies>(this.filters, 'sortBy');
        this.filterService.deserialize<IFilterValueCompanies>(this.filters, 'sortDirection');
        this.filterService.deserialize<IFilterValueCompanies>(this.filters, 'searchText');
        await this.filterService.deserializeTypeAhead<IFilterValueCompanies, ContactView>(
            this.filters,
            'contact',
            this.contactServiceFilter as IGetById
        );
        this.filterService.deserializeArray<IFilterValueCompanies, CompanyTypeView>(
            this.filters,
            'companyType',
            'id',
            this.companyTypes ?? []
        );
        this.filterService.deserializeArray<IFilterValueCompanies, MarketView>(
            this.filters,
            'market',
            'id',
            this.markets ?? []
        );
        this.filterService.deserializeBoolean<IFilterValueCompanies>(this.filters, 'includeArchived');
        await this.filterService.deserializeTypeAhead<IFilterValueCompanies, UserView>(
            this.filters,
            'salespersons',
            this.userService as IGetById,
            true
        );
    }

    private someFilterApplied(): boolean {
        return Object.values(this.filters).some((f) => (Array.isArray(f) ? f.length : f));
    }
}
